import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
} from '@angular/core';
import {NgChanges} from 'amc-common-components';
import {LeftPanelOutput, Page, SubPage, Vendor} from '@entities/left-panel/left-panel.interface';
import {MDM_COLORS} from '@entities/common/colors';

/**
 * Left panel component.
 * @class
 */
@Component({
	selector: 'left-panel-component',
	templateUrl: './left-panel.component.html',
	styleUrls: ['./left-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeftPanelComponent implements OnChanges {
	/**
	 * Input variable for providing 'pages' value to component from parent.
	 */
	@Input() pages: Page[];
	/**
	 * Input variable for providing 'active page' value to component from parent.
	 */
	@Input() activePage: Page;
	/**
	 * Input variable for providing 'active sub page' value to component from parent.
	 */
	@Input() activeSubPage: SubPage;
	/**
	 * Input variable for providing 'active vendor' value to component from parent.
	 */
	@Input() activeVendor: Vendor;

	/**
	 * Output variable for emitting 'choose page' value from component to parent.
	 */
	@Output() choosePage = new EventEmitter<LeftPanelOutput>();

	/** Auxiliary 'openedSubPageUrl' variable. */
	openedSubPageUrl: string = null;
	/** Auxiliary 'mdmColors' variable. */
	mdmColors = MDM_COLORS;

	/**
	 * Constructor for LeftPanelComponent.
	 */
	constructor(private cd: ChangeDetectorRef) {}

	/**
	 * Default Angular framework 'ngOnChanges' Lifecycle hook.
	 * @param {NgChanges<LeftPanelComponent>} changes
	 * @returns {void}
	 */
	ngOnChanges(changes: NgChanges<LeftPanelComponent>): void {
		if (changes.activeSubPage?.currentValue) {
			this.openedSubPageUrl = this.activeSubPage.url;
		}
	}

	/**
	 * Open close sub page.
	 * @param {SubPage} subPage
	 * @returns {void}
	 */
	openCloseSubPage(subPage: SubPage): void {
		this.openedSubPageUrl = this.isSubPageOpened(subPage) ? null : subPage.url;
		this.cd.detectChanges();
	}

	/**
	 * Check is sub page opened.
	 * @param {SubPage} subPage
	 * @returns {boolean}
	 */
	isSubPageOpened(subPage: SubPage): boolean {
		return this.openedSubPageUrl === subPage.url;
	}

	/**
	 * On page change.
	 * @param {Page} page
	 * @param {SubPage} subPage
	 * @param {Vendor} vendor
	 * @returns {void}
	 */
	onPageChange(page: Page, subPage?: SubPage, vendor?: Vendor): void {
		if (page.disabled && !subPage) {
			return;
		}

		this.choosePage.emit({
			page,
			subPage,
			vendor,
		});
	}
}
